<!-- eslint-disable vue/multi-word-component-names -->
<script setup>
import {
  ref,
  watch,
  onMounted,
  onBeforeUnmount,
  nextTick,
} from "vue";
import { useRoute, useRouter } from "vue-router";
import SearchModal from "./SearchModal.vue";
import { getAllArticles } from "../utils/articles.js";

const route = useRoute();
const router = useRouter();

const isSearchOpen = ref(false);
const mobileNavOpen = ref(false);
const articles = ref({});

/** Which section is in view for nav highlight: '', 'about', 'approach', 'services', 'articles' */
const activeNavSection = ref("");

const navLinkClass = (section) => {
  const active =
    activeNavSection.value === section
      ? "border-b-2 border-primary pb-1 font-bold text-primary opacity-100"
      : "border-b-2 border-transparent pb-1 font-medium text-on-background opacity-60 hover:text-primary hover:opacity-100";
  return `cursor-pointer font-label text-label-md uppercase tracking-tighter transition-all duration-500 ease-expo ${active}`;
};

const updateActiveNavFromScroll = () => {
  if (route.path !== "/") return;
  const anchor = window.scrollY + 140;
  const ordered = ["blogs", "about", "approach", "services", "articles"];
  for (let i = ordered.length - 1; i >= 0; i--) {
    const id = ordered[i];
    const el = document.getElementById(id);
    if (!el) continue;
    const top = el.getBoundingClientRect().top + window.scrollY;
    if (anchor >= top) {
      activeNavSection.value = id === "blogs" ? "" : id;
      return;
    }
  }
  activeNavSection.value = "";
};

const scrollToSection = (id) => {
  mobileNavOpen.value = false;
  if (id === "articles" && route.path !== "/") {
    router.push("/articles");
    return;
  }
  if (route.path === "/") {
    document.getElementById(id)?.scrollIntoView({ behavior: "smooth" });
    return;
  }
  router.push({ path: "/", hash: `#${id}` });
};

const openSearch = () => {
  isSearchOpen.value = true;
  mobileNavOpen.value = false;
};

const closeSearch = () => {
  isSearchOpen.value = false;
};

const onScrollNav = () => updateActiveNavFromScroll();

watch(
  () => route.path,
  (path) => {
    window.removeEventListener("scroll", onScrollNav);
    if (path === "/") {
      window.addEventListener("scroll", onScrollNav, { passive: true });
      nextTick(() => updateActiveNavFromScroll());
    } else {
      activeNavSection.value = "";
    }
  },
  { immediate: true },
);

onMounted(async () => {
  try {
    const articlesObj = await getAllArticles();
    articles.value = articlesObj || {};
  } catch {
    articles.value = {};
  }
});

onBeforeUnmount(() => {
  window.removeEventListener("scroll", onScrollNav);
});
</script>

<template>
  <header
    class="fixed top-0 z-50 w-full border-b border-outline-variant/15 bg-white transition-all duration-500"
  >
    <div
      class="mx-auto flex w-full max-w-[1920px] items-center justify-between px-4 py-5 md:px-8 md:py-8 lg:px-12 lg:py-10"
    >
      <router-link to="/" class="flex shrink-0 items-center gap-2">
        <div class="h-9 w-32 overflow-hidden rounded-lg sm:h-10 sm:w-36 md:h-12 md:w-48">
          <img
            src="/images/bi-logo.jpg"
            alt="Brand Integrated"
            class="h-full w-full object-contain object-left"
          />
        </div>
      </router-link>

      <div class="hidden items-center space-x-12 md:flex">
        <a
          :class="navLinkClass('about')"
          href="#about"
          @click.prevent="scrollToSection('about')"
          >About</a
        >
        <a
          :class="navLinkClass('approach')"
          href="#approach"
          @click.prevent="scrollToSection('approach')"
          >Approach</a
        >
        <a
          :class="navLinkClass('services')"
          href="#services"
          @click.prevent="scrollToSection('services')"
          >Services</a
        >
        <a
          :class="navLinkClass('articles')"
          href="#articles"
          @click.prevent="scrollToSection('articles')"
          >Articles</a
        >
      </div>

      <div class="flex items-center gap-2 md:gap-4">
        <button
          type="button"
          class="inline-flex btn-header-search"
          aria-label="Search articles"
          @click="openSearch"
        >
          <span class="material-symbols-outlined text-[22px]">search</span>
        </button>
        <a
          href="mailto:info@brandintegrated.com"
          class="inline-flex btn-header-primary md:hidden"
          aria-label="Contact us"
        >
          <span class="material-symbols-outlined text-[22px]" aria-hidden="true">mail</span>
        </a>
        <a
          href="mailto:info@brandintegrated.com"
          class="btn-header-text-primary hidden md:inline-flex"
        >
          Contact
        </a>
        <button
          type="button"
          class="inline-flex btn-header-search md:hidden"
          aria-label="Open menu"
          @click="mobileNavOpen = !mobileNavOpen"
        >
          <span class="material-symbols-outlined text-on-background">{{
            mobileNavOpen ? "close" : "menu"
          }}</span>
        </button>
      </div>
    </div>

    <div
      v-if="mobileNavOpen"
      class="border-t border-outline-variant/20 bg-white px-4 py-4 md:hidden"
    >
      <div
        class="flex flex-col gap-4 font-body text-sm uppercase tracking-tighter"
      >
        <a
          :class="
            activeNavSection === 'about'
              ? 'font-bold text-primary'
              : 'text-on-background opacity-80'
          "
          href="#about"
          @click.prevent="scrollToSection('about')"
          >About</a
        >
        <a
          :class="
            activeNavSection === 'approach'
              ? 'font-bold text-primary'
              : 'text-on-background opacity-80'
          "
          href="#approach"
          @click.prevent="scrollToSection('approach')"
          >Approach</a
        >
        <a
          :class="
            activeNavSection === 'services'
              ? 'font-bold text-primary'
              : 'text-on-background opacity-80'
          "
          href="#services"
          @click.prevent="scrollToSection('services')"
          >Services</a
        >
        <a
          :class="
            activeNavSection === 'articles'
              ? 'font-bold text-primary'
              : 'text-on-background opacity-80'
          "
          href="#articles"
          @click.prevent="scrollToSection('articles')"
          >Articles</a
        >
      </div>
    </div>

    <SearchModal
      :is-open="isSearchOpen"
      :articles="articles"
      @close="closeSearch"
    />
  </header>
</template>
